import { KatInput, KatButton, KatIcon } from '@amzn/katal-react';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { useState } from 'react';
import {
  TargetSegment,
  SegmentKey,
  SelectionType,
} from '@amzn/fire-tv-campaign-automation-service';
import { StateConstants, WeblabConstants } from 'src/constants';
import styles from './WeblabInput.module.css';
import WeblabSelectedItemsList from './components/WeblabSelectedItemsList';

interface WeblabInputProps {
  weblabSegments: TargetSegment[];
  onWeblabSegmentsChange: (segments: TargetSegment[]) => void;
}

export const WeblabInput = ({ weblabSegments, onWeblabSegmentsChange }: WeblabInputProps) => {
  const [weblabId, setWeblabId] = useState('');
  const [treatment, setTreatment] = useState('C');
  const [selectionType, setSelectionType] = useState<SelectionType>(SelectionType.INCLUDE);
  const [error, setError] = useState('');

  // Validate incoming weblab segments
  const invalidSegments = weblabSegments.filter(
    (segment) =>
      !segment.segmentId ||
      !segment.treatment ||
      !Object.values(SelectionType).includes(segment.selectionType),
  );

  if (invalidSegments.length > 0) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{WeblabConstants.ERROR_MESSAGES.INVALID_WEBLAB_SEGMENTS}</div>
      </div>
    );
  }

  const handleWeblabAdd = () => {
    const trimmedId = weblabId.trim();

    if (!trimmedId) {
      setError(WeblabConstants.ERROR_MESSAGES.WEBLAB_ID_REQUIRED);
      return;
    }

    const existingIds = new Set(weblabSegments.map((s) => s.segmentId));

    if (existingIds.has(trimmedId)) {
      setError(WeblabConstants.ERROR_MESSAGES.WEBLAB_ID_ALREADY_EXISTS);
      return;
    }

    const newWeblab: TargetSegment = {
      segmentId: trimmedId,
      segmentKey: SegmentKey.WEBLAB_SERVICE,
      selectionType,
      treatment,
    };

    onWeblabSegmentsChange([...weblabSegments, newWeblab]);
    setWeblabId(StateConstants.EMPTY_STRING);
    setError(StateConstants.EMPTY_STRING);
  };

  const handleWeblabRemove = (index: number) => {
    onWeblabSegmentsChange(weblabSegments.filter((_, i) => i !== index));
  };

  return (
    <div className={styles.container}>
      <div className={styles.inputRow}>
        <KatInput
          type="text"
          label="Weblab ID"
          value={weblabId}
          onInput={(e) => {
            setWeblabId(e.currentTarget.value);
            setError('');
          }}
          onKeydown={(e: KatInput.KeydownEvent) => {
            const key = 'key' in e ? e.key : undefined;
            if (key === 'Enter' && weblabId.trim()) {
              if ('preventDefault' in e) e.preventDefault();
              handleWeblabAdd();
            }
          }}
          placeholder="Enter weblab ID"
          className={styles.weblabInput}
          data-testid="weblab-id-input"
          maxlength={WeblabConstants.WEBLAB_ID_MAX_LENGTH}
        />

        <StyledDropdown
          label="Treatment"
          value={treatment}
          onChange={(e) => setTreatment(e.detail.value)}
          options={WeblabConstants.TREATMENT_OPTIONS}
          className={styles.treatmentDropdown}
          data-testid="main-treatment-dropdown"
        />

        <StyledDropdown
          label="Type"
          value={selectionType}
          onChange={(e) => setSelectionType(e.detail.value as SelectionType)}
          options={WeblabConstants.SELECTION_OPTIONS}
          className={styles.typeDropdown}
          data-testid="main-type-dropdown"
        />
      </div>
      <div className={styles.inputRow}>
        <KatButton
          variant="primary"
          onClick={handleWeblabAdd}
          disabled={!weblabId.trim()}
          className={styles.addButton}
          data-testid="add-weblab-button"
          aria-label="Add weblab"
        >
          {WeblabConstants.BUTTON_TEXT.ADD_WEBLAB}
        </KatButton>
      </div>

      {error && (
        <div className={styles.error} data-testid="weblab-error">
          {error}
        </div>
      )}

      <WeblabSelectedItemsList weblabs={weblabSegments} onRemoveItem={handleWeblabRemove} />
    </div>
  );
};
